import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useContext,
} from "react";
import { useQuery } from "@tanstack/react-query";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import styles from "../../css/heatmaps/ModalCreditLogs.module.css";
import { AppContext } from "../../context/AppContext";

// --- Pomocné komponenty a funkce ---

const ActionIcon = ({ action }) => {
  const isAddition = action.toLowerCase().includes("přidání");
  const iconColor = isAddition ? "#22c55e" : "#ef4444";
  const iconPath = isAddition ? "M12 4.5v15m7.5-7.5h-15" : "M19.5 12h-15";

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2.5}
      stroke={iconColor}
      className={styles.thumbnailImage}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
    </svg>
  );
};

const formatDateTime = (dateString) => {
  return new Intl.DateTimeFormat("cs-CZ", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(dateString));
};

const formatDateForPicker = (date) => {
  if (!date) return "dd.mm.rrrr";
  return date.toLocaleDateString("cs-CZ");
};

async function fetchCreditLogs() {
  const res = await fetch("/data/dummy_credit_logs.json", {
    cache: "no-cache",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return data.map((item, index) => ({ ...item, id: `${item.date}-${index}` }));
}

// Komponenta pro filtry
const CreditLogFilters = ({ filters, onFilterChange, onReset }) => {
  const [searchInput, setSearchInput] = useState(filters.searchTerm);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const searchTimeoutRef = useRef(null);
  const dateRangeRef = useRef(null);

  const handleSearchChange = useCallback(
    (value) => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => {
        onFilterChange({ ...filters, searchTerm: value });
      }, 300);
    },
    [filters, onFilterChange]
  );

  useEffect(() => {
    if (!isCalendarOpen) return;
    const handleClickOutside = (e) => {
      if (dateRangeRef.current && !dateRangeRef.current.contains(e.target)) {
        setIsCalendarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isCalendarOpen]);

  const handleReset = () => {
    setSearchInput("");
    onReset();
  };

  return (
    <div className={styles.controlsHeader}>
      <div className={styles.filtersRowInline}>
        <input
          type="text"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value);
            handleSearchChange(e.target.value);
          }}
          placeholder="Vyhledat..."
          className={styles.searchInput}
        />
        <select
          className={styles.sortSelect}
          value={filters.sortBy}
          onChange={(e) =>
            onFilterChange({ ...filters, sortBy: e.target.value })
          }
        >
          <option value="date_desc">Nejnovější</option>
          <option value="date_asc">Nejstarší</option>
          <option value="email_asc">Email (A-Z)</option>
          <option value="email_desc">Email (Z-A)</option>
        </select>
        <div className={styles.dateInputsGroup} ref={dateRangeRef}>
          <div
            className={styles.dateInputWrap}
            onClick={() => setIsCalendarOpen(!isCalendarOpen)}
          >
            <input
              type="text"
              readOnly
              value={`${formatDateForPicker(
                filters.dateRange[0]
              )} - ${formatDateForPicker(filters.dateRange[1])}`}
              className={styles.dateInput}
            />
            <span className={styles.calendarIcon} />
          </div>
          {isCalendarOpen && (
            <div className={styles.calendarContainerInline}>
              <Calendar
                onChange={(value) => {
                  onFilterChange({ ...filters, dateRange: value });
                  setIsCalendarOpen(false);
                }}
                selectRange
                value={filters.dateRange}
              />
            </div>
          )}
        </div>
        <button
          type="button"
          className={styles.clearLink}
          onClick={handleReset}
        >
          Smazat&nbsp;filtr
        </button>
      </div>
    </div>
  );
};

// Vlastní hook pro sledování šířky okna
const useWindowWidth = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return windowWidth;
};

// --- Hlavní komponenta ---
const ModalCreditLogs = () => {
  const [selectedLog, setSelectedLog] = useState(null);
  const [filters, setFilters] = useState({
    searchTerm: "",
    sortBy: "date_desc",
    dateRange: [null, null],
  });

  const windowWidth = useWindowWidth();
  const isMobileView = windowWidth < 900;
  const [isMobileDetailVisible, setIsMobileDetailVisible] = useState(false);
  const [areFiltersVisible, setAreFiltersVisible] = useState(false);

  const { setShowCreditLogsModal } = useContext(AppContext);

  const {
    data: logs,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["creditLogs"],
    queryFn: fetchCreditLogs,
  });

  const filteredLogs = useMemo(() => {
    if (!logs) return [];
    let processedData = [...logs];
    const { searchTerm, dateRange, sortBy } = filters;
    if (searchTerm) {
      const lowercasedTerm = searchTerm.toLowerCase();
      processedData = processedData.filter((log) =>
        [log.email, log.action, log.reason, log.description, log.assignedTo]
          .join(" ")
          .toLowerCase()
          .includes(lowercasedTerm)
      );
    }
    if (dateRange[0] && dateRange[1]) {
      const [startDate, endDate] = dateRange;
      endDate.setHours(23, 59, 59, 999);
      processedData = processedData.filter((log) => {
        const logDate = new Date(log.date);
        return logDate >= startDate && logDate <= endDate;
      });
    }
    processedData.sort((a, b) => {
      switch (sortBy) {
        case "date_asc":
          return new Date(a.date) - new Date(b.date);
        case "email_asc":
          return a.email.localeCompare(b.email);
        case "email_desc":
          return b.email.localeCompare(a.email);
        default:
          return new Date(b.date) - new Date(a.date);
      }
    });
    return processedData;
  }, [logs, filters]);

  useEffect(() => {
    if (!isMobileView && filteredLogs?.length > 0) {
      const isSelectedLogStillPresent = filteredLogs.some(
        (log) => log.id === selectedLog?.id
      );
      if (!isSelectedLogStillPresent) {
        setSelectedLog(filteredLogs[0]);
      }
    }
  }, [filteredLogs, isMobileView, selectedLog]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    if (isMobileView) {
      setIsMobileDetailVisible(false);
    }
  };

  const handleResetFilters = () => {
    setFilters({
      searchTerm: "",
      sortBy: "date_desc",
      dateRange: [null, null],
    });
    if (isMobileView) {
      setIsMobileDetailVisible(false);
    }
  };

  const handleLogSelect = (log) => {
    setSelectedLog(log);
    if (isMobileView) {
      setIsMobileDetailVisible(true);
    }
  };

  const selectedLogDetails = useMemo(() => {
    if (!selectedLog) return null;
    const isAddition = selectedLog.action.toLowerCase().includes("přidání");
    return {
      ...selectedLog,
      formattedDate: formatDateTime(selectedLog.date),
      amountSign: isAddition ? "+" : "-",
      amountClass: isAddition ? styles.amountPositive : styles.amountNegative,
    };
  }, [selectedLog]);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const modalContentClass = `${styles.modalContent} ${
    isMobileView && isMobileDetailVisible ? styles.isMobileDetailActive : ""
  }`;
  const filtersContainerClass = `${styles.filtersContainer} ${
    areFiltersVisible ? styles.visible : ""
  }`;

  return (
    <div className={styles.modalOverlay}>
      <div className={modalContentClass}>
        <aside className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <h2 className={styles.sidebarTitle}>Historie transakcí</h2>
            {isMobileView && (
              <button
                className={styles.toggleFiltersButton}
                onClick={() => setAreFiltersVisible(!areFiltersVisible)}
              >
                {areFiltersVisible ? "Skrýt" : "Filtry"}
              </button>
            )}
            <button
              onClick={() => setShowCreditLogsModal(false)}
              className={styles.closeButtonMobile}
            >
              &times;
            </button>
          </div>

          {isMobileView && (
            <div className={filtersContainerClass}>
              <CreditLogFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onReset={handleResetFilters}
              />
            </div>
          )}

          <div className={styles.sidebarCount}>
            Nalezeno: {filteredLogs.length}
          </div>
          <ul className={styles.thumbnailList}>
            {isLoading && <p>Načítání logů...</p>}
            {isError && <p>Chyba při načítání dat.</p>}
            {filteredLogs.map((log) => (
              <li
                key={log.id}
                className={`${styles.thumbnailItem} ${
                  selectedLog?.id === log.id && !isMobileView
                    ? styles.active
                    : ""
                }`}
                onClick={() => handleLogSelect(log)}
              >
                <ActionIcon action={log.action} />
                <div className={styles.thumbnailInfo}>
                  <span className={styles.thumbnailAction}>{log.action}</span>
                  <span className={styles.thumbnailDate}>
                    {formatDateTime(log.date)}
                  </span>
                </div>
              </li>
            ))}
            {!isLoading && !filteredLogs.length && (
              <li className={styles.noResults}>Žádné výsledky</li>
            )}
          </ul>
        </aside>

        <main className={styles.mainContent}>
          <div className={styles.header}>
            {isMobileView && (
              <button
                onClick={() => setIsMobileDetailVisible(false)}
                className={styles.backButtonMobile}
              >
                &larr; Zpět na výpis
              </button>
            )}
            <h1 className={styles.mainTitle}>Detail transakce</h1>
            <button
              onClick={() => setShowCreditLogsModal(false)}
              className={styles.closeButton}
            >
              Zavřít
            </button>
          </div>

          {!isMobileView && (
            <CreditLogFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onReset={handleResetFilters}
            />
          )}

          <div className={styles.tabs}>
            <button className={`${styles.tabButton} ${styles.activeTab}`}>
              Přehled
            </button>
          </div>
          <div className={styles.tabContent}>
            {!selectedLogDetails ? (
              <div className={styles.noSelection}>
                <p>Vyberte transakci z levého panelu.</p>
              </div>
            ) : (
              <div className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Email</span>
                  <span className={styles.detailValue}>
                    {selectedLogDetails.email}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Akce</span>
                  <span className={styles.detailValue}>
                    {selectedLogDetails.action}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Datum a čas</span>
                  <span className={styles.detailValue}>
                    {selectedLogDetails.formattedDate}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Změna</span>
                  <span
                    className={`${styles.detailValue} ${selectedLogDetails.amountClass}`}
                  >
                    {selectedLogDetails.amountSign}
                    {selectedLogDetails.amount} kredit(ů)
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Stav před/po</span>
                  <span className={styles.detailValue}>
                    {selectedLogDetails.before} &rarr;{" "}
                    {selectedLogDetails.after}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Důvod</span>
                  <span className={styles.detailValue}>
                    {selectedLogDetails.reason}
                  </span>
                </div>
                {selectedLogDetails.assignedTo && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Přiřazeno</span>
                    <span className={styles.detailValue}>
                      {selectedLogDetails.assignedTo}
                    </span>
                  </div>
                )}
                <div className={`${styles.detailItem} ${styles.fullWidth}`}>
                  <span className={styles.detailLabel}>Popis</span>
                  <span className={styles.detailValue}>
                    {selectedLogDetails.description}
                  </span>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ModalCreditLogs;
